import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../lib/axios';
import type {
  SubscriptionPlan,
  CreatePlanDto,
  UpdatePlanDto,
  PlanModule,
  AssignModuleToPlanDto,
  UpdatePlanModuleDto,
} from '../../types/subscriptions';

const QUERY_KEYS = {
  plans: ['subscription-plans'] as const,
  plan: (id: string) => ['subscription-plans', id] as const,
  planModules: (planId: string) => ['subscription-plans', planId, 'modules'] as const,
};

export const usePlans = () => {
  return useQuery({
    queryKey: QUERY_KEYS.plans,
    queryFn: async () => {
      const { data } = await axiosInstance.get<SubscriptionPlan[]>('/subscriptions/plans');
      return data;
    },
  });
};

export const usePlan = (id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.plan(id),
    queryFn: async () => {
      const { data } = await axiosInstance.get<SubscriptionPlan>(`/subscriptions/plans/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

export const useCreatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (plan: CreatePlanDto) => {
      const { data } = await axiosInstance.post<SubscriptionPlan>('/subscriptions/plans', plan);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

export const useUpdatePlan = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (plan: UpdatePlanDto) => {
      const { data } = await axiosInstance.patch<SubscriptionPlan>(`/subscriptions/plans/${id}`, plan);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plan(id) });
    },
  });
};

export const useDeletePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`/subscriptions/plans/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plans });
    },
  });
};

// ============= PLAN MODULES HOOKS =============

export const usePlanModules = (planId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.planModules(planId),
    queryFn: async () => {
      const { data } = await axiosInstance.get<PlanModule[]>(
        `/subscriptions/plans/${planId}/modules`
      );
      return data;
    },
    enabled: !!planId,
  });
};

export const useAssignModuleToPlan = (planId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: AssignModuleToPlanDto) => {
      const { data } = await axiosInstance.post<PlanModule>(
        `/subscriptions/plans/${planId}/modules`,
        dto
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plan(planId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.planModules(planId) });
    },
  });
};

export const useUpdatePlanModule = (planId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      moduleId,
      dto,
    }: {
      moduleId: string;
      dto: UpdatePlanModuleDto;
    }) => {
      const { data } = await axiosInstance.patch<PlanModule>(
        `/subscriptions/plans/${planId}/modules/${moduleId}`,
        dto
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plan(planId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.planModules(planId) });
    },
  });
};

export const useRemoveModuleFromPlan = (planId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (moduleId: string) => {
      await axiosInstance.delete(
        `/subscriptions/plans/${planId}/modules/${moduleId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.plan(planId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.planModules(planId) });
    },
  });
};
